import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import Logo from '../components/Logo';
import { ArrowLeft } from 'lucide-react';

const SignupPage = () => {
  const navigate = useNavigate();
  const { signup } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    role: '',
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.role) {
      toast.error('Please select your role');
      return;
    }

    setLoading(true);

    try {
      await signup(formData.email, formData.password, formData.full_name, formData.role);
      toast.success('Account created successfully!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create account');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <Button
          variant="ghost"
          onClick={() => navigate('/')}
          className="mb-8"
          data-testid="back-button"
        >
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Home
        </Button>

        <div className="bg-white rounded-xl border border-border p-8 shadow-sm">
          <div className="mb-8 text-center">
            <Logo className="h-10 mx-auto mb-4 object-contain" />
            <h1 className="text-3xl font-heading font-bold text-primary mb-2" data-testid="signup-title">
              Join TaskPiece
            </h1>
            <p className="text-muted-foreground" data-testid="signup-subtitle">
              Create your account to get started
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6" data-testid="signup-form">
            <div>
              <Label htmlFor="full_name" data-testid="name-label">
                Full Name
              </Label>
              <Input
                id="full_name"
                type="text"
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                required
                className="mt-2"
                data-testid="name-input"
                placeholder="John Doe"
              />
            </div>

            <div>
              <Label htmlFor="email" data-testid="email-label">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="mt-2"
                data-testid="email-input"
                placeholder="you@example.com"
              />
            </div>

            <div>
              <Label htmlFor="mobile" data-testid="mobile-label">
                Mobile Number <span className="text-xs text-muted-foreground">(optional)</span>
              </Label>
              <div className="flex mt-2 gap-2">
                <div className="flex items-center px-3 bg-slate-50 border rounded-md text-sm text-muted-foreground">+91</div>
                <Input
                  id="mobile"
                  type="tel"
                  value={formData.mobile || ''}
                  onChange={(e) => setFormData({ ...formData, mobile: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                  className="flex-1"
                  data-testid="mobile-input"
                  placeholder="9876543210"
                  maxLength={10}
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password" data-testid="password-label">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                minLength={6}
                className="mt-2"
                data-testid="password-input"
                placeholder="••••••••"
              />
            </div>

            <div>
              <Label htmlFor="role" data-testid="role-label">
                I want to...
              </Label>
              <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })}>
                <SelectTrigger className="mt-2" data-testid="role-select">
                  <SelectValue placeholder="Select your role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="freelancer" data-testid="role-freelancer">
                    Work as a Freelancer
                  </SelectItem>
                  <SelectItem value="client" data-testid="role-client">
                    Hire Talent
                  </SelectItem>
                  <SelectItem value="corporate" data-testid="role-corporate">
                    Corporate/Bulk Hiring
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90 h-12 rounded-lg font-medium shadow-sm hover:shadow-md"
              data-testid="signup-submit-button"
            >
              {loading ? 'Creating account...' : 'Create Account'}
            </Button>
          </form>

          <div className="mt-6 text-center text-sm">
            <span className="text-muted-foreground">Already have an account? </span>
            <Link to="/login" className="text-secondary font-medium hover:underline" data-testid="login-link">
              Sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignupPage;
